import { Reducer } from 'redux';
import { UIState } from './types';
import { StudentActionTypes } from '../student/types';
import { AuthActionTypes } from '../auth/types';
import { UIActionTypes } from './types';
import { DataActionTypes } from '../data/types';
import { Status, SectionId } from '../../components/molecules/Sidebar/types';
import deepmerge from 'deepmerge';
import { defaultConfig } from '../config/reducer';
import { API_CALLS } from '../../utilities/constants';
import { toast } from 'react-toastify';
import * as types from 'types';
import { OfferActionTypes } from '../offers/types';
import { EnrolmentStatusEnum } from '../student/offer/types';

const defaultChecklistSection = {
  id: SectionId.CHECKLIST,
  label: 'CAS Checklist',
  subLabel: 'Incomplete',
  description: '',
  status: Status.Available,
  clickable: true
};

const defaultDocumentSection = {
  id: SectionId.GENERAL_DOC,
  label: 'General Documents',
  description: 'Please upload or review document requirements as requested',
  status: Status.Disabled,
  clickable: false
};

const defaultFinanceSection = {
  id: SectionId.FINANCE_DOC,
  label: 'Financial Documents',
  description: 'Please upload or review document requirements as requested',
  status: Status.Disabled,
  hidden: false,
  clickable: false
};

const defaultPreCasSection = {
  id: SectionId.PRE_CAS_INTERVIEW,
  label: 'Pre-CAS Questionnaire',
  description: 'Answer a few questions about your upcoming overseas study',
  status: Status.Disabled,
  clickable: false
};

const defaultScheduledInterviewsSection = {
  id: SectionId.SCHEDULED_INTERVIEWS,
  label: 'Interviews',
  description: 'Please review this section to finalize any interview requirements',
  status: Status.Disabled,
  clickable: false
};

const defaultVisaAppSection = {
  id: SectionId.VISA_PREP,
  label: 'Start Visa Application',
  description: 'Begin the visa and immigration process',
  status: Status.Disabled,
  clickable: false
};

const defaultCasDocSection = {
  id: SectionId.CAS_DOC,
  label: 'Receive CAS Document',
  description: 'Your CAS will not be issued until the above tasks are completed',
  status: Status.Disabled,
  clickable: false,
  hidden: true
};

const defaultEnrolmentSection = {
  id: SectionId.ENROLMENT,
  label: 'Enrolment Status',
  description: 'Please ensure the details of your visa and travel are up to date',
  clickable: true,
  status: EnrolmentStatusEnum.None,
  hidden: false
};

const defaultPreDepartureDocumentsSection = {
  id: SectionId.PRE_DEPARTURE_DOCS,
  label: 'Pre-Departure & Travel',
  description: 'Please read and complete these tasks before you travel',
  clickable: true,
  status: types.TravelStatusEnum.None,
  hidden: false
};

const defaultAfterTravelDocumentsSection = {
  id: SectionId.AFTER_TRAVEL_DOCS,
  label: 'After Travel',
  description: 'Complete these tasks once you arrive ',
  clickable: true,
  status: types.TravelStatusEnum.None,
  hidden: false
};

const initialSections = [
  defaultChecklistSection,
  defaultDocumentSection,
  defaultFinanceSection,
  defaultPreCasSection,
  defaultScheduledInterviewsSection,
  defaultVisaAppSection,
  defaultCasDocSection,
  defaultEnrolmentSection,
  defaultPreDepartureDocumentsSection,
  defaultAfterTravelDocumentsSection
];

const initialState: UIState = {
  isLoading: false,
  openModal: null,
  sections: initialSections,
  taskMenuOpen: false,
  activeSectionId: undefined,
  isTawkLoaded: false,
  initNotificationsShown: false
};

const fullPageLoadAPICalls = [API_CALLS.GET_STUDENT_RECORD];

const reducer: Reducer<UIState> = (state = initialState, action) => {
  switch (action.type) {
    case AuthActionTypes.COGNITO_LOGIN_REQUEST:
    case AuthActionTypes.COGNITO_REAUTH_REQUEST:
    case AuthActionTypes.FORGOTTEN_PASSWORD_REQUEST:
    case AuthActionTypes.RESET_PASSWORD_REQUEST:
    case AuthActionTypes.CHANGE_PASSWORD_REQUEST:
    case StudentActionTypes.GET_STUDENT_RECORD:
    case StudentActionTypes.SAVE_DEPOSIT:
    case StudentActionTypes.CREATE_ORGANISATION_SPONSOR:
    case StudentActionTypes.UPDATE_ORGANISATION_SPONSOR:
    case StudentActionTypes.REMOVE_ORGANISATION_SPONSOR:
    case StudentActionTypes.REMOVE_ORGANISATION_REGION:
    case StudentActionTypes.CREATE_EDUCATION_SPONSOR:
    case StudentActionTypes.UPDATE_EDUCATION_SPONSOR:
    case StudentActionTypes.REMOVE_EDUCATION_SPONSOR:
    case StudentActionTypes.REMOVE_EDUCATION_REGION:
    case StudentActionTypes.CREATE_SCHOLARSHIP_SPONSOR:
    case StudentActionTypes.UPDATE_SCHOLARSHIP_SPONSOR:
    case StudentActionTypes.BULK_UPDATE_SCHOLARSHIP_SPONSOR:
    case StudentActionTypes.REMOVE_SCHOLARSHIP_SPONSOR:
    case StudentActionTypes.REMOVE_SCHOLARSHIP_REGION:
    case StudentActionTypes.CREATE_FAMILY_SPONSOR:
    case StudentActionTypes.UPDATE_FAMILY_SPONSOR:
    case StudentActionTypes.REMOVE_FAMILY_SPONSOR:
    case StudentActionTypes.REMOVE_FAMILY_REGION:
    case StudentActionTypes.CREATE_OTHER_SPONSOR:
    case StudentActionTypes.UPDATE_OTHER_SPONSOR:
    case StudentActionTypes.REMOVE_OTHER_SPONSOR:
    case StudentActionTypes.REMOVE_OTHER_REGION:
    case OfferActionTypes.SET_ACTIVE_OFFER:
    case OfferActionTypes.GET_ACTIVE_OFFERS:
    case AuthActionTypes.STUDENT_MFA_STATUS_REQUEST:
      return { ...state, isLoading: true };

    case OfferActionTypes.GET_ACTIVE_OFFERS_FAILURE:
    case AuthActionTypes.COGNITO_LOGIN_FAILURE:
    case AuthActionTypes.COGNITO_REAUTH_FAILURE:
    case AuthActionTypes.FORGOTTEN_PASSWORD_SUCCESS:
    case AuthActionTypes.FORGOTTEN_PASSWORD_FAILURE:
    case AuthActionTypes.RESET_PASSWORD_SUCCESS:
    case AuthActionTypes.RESET_PASSWORD_FAILURE:
    case AuthActionTypes.CHANGE_PASSWORD_SUCCESS:
    case AuthActionTypes.CHANGE_PASSWORD_FAILURE:
    case StudentActionTypes.SAVE_FINANCE_SUCCESS:
    case OfferActionTypes.SET_ACTIVE_OFFER_SUCCESS:
    case OfferActionTypes.SET_ACTIVE_OFFER_FAILURE:
    case AuthActionTypes.STUDENT_MFA_STATUS_SUCCESS:
    case StudentActionTypes.SAVE_PERSONAL_INFO_FAILURE:
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_FAILURE:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_FAILURE:
    case AuthActionTypes.COGNITO_LOGIN_SUCCESS:
    case AuthActionTypes.COGNITO_REAUTH_SUCCESS:
    case AuthActionTypes.COGNITO_LOGIN_CHALLENGE:
      return { ...state, isLoading: false };
    case DataActionTypes.RETRY_API_CALL: {
      const { callName } = action.payload;
      if (fullPageLoadAPICalls.includes(callName)) {
        return { ...state, isLoading: true };
      }
      return state;
    }
    case UIActionTypes.SET_CONTEXT_LOADING: {
      const isLoading = action.payload.isLoading;
      return {
        ...state,
        isContextLoading: isLoading
      };
    }

    case UIActionTypes.SET_CONTEXT_SUCCESS: {
      return {
        ...state,
        isContextLoading: false
      };
    }
    case UIActionTypes.SET_IS_LOADING: {
      const { isLoading } = action.payload;
      return {
        ...state,
        isLoading
      };
    }
    // TODO: Will need to change this when we have active intake selector
    case OfferActionTypes.GET_ACTIVE_OFFERS_SUCCESS: {
      return { ...state, isLoading: false };
    }
    case DataActionTypes.RETRY_API_CALL_SUCCESS: {
      const callName = action.payload;
      if (fullPageLoadAPICalls.includes(callName)) {
        return { ...state, isLoading: false };
      }
      return state;
    }
    case UIActionTypes.OPEN_DOCUMENT_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, isInfoOnly, modalTitle = '' } = action.payload;
      return {
        ...state,
        openModal: {
          ...state.openModal,
          sponsorType,
          docType,
          sponsorKey,
          docKey,
          modalTitle,
          isInfoOnly,
          isFinanceDocumentModal: false,
          isFollowUpModal: false,
          isUpload: false
        }
      };
    }
    case UIActionTypes.OPEN_FINANCE_DOCUMENT_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, isInfoOnly, modalTitle = '' } = action.payload;
      return {
        ...state,
        openModal: {
          ...state.openModal,
          sponsorType,
          docType,
          sponsorKey,
          docKey,
          modalTitle,
          isInfoOnly,
          isFinanceDocumentModal: true,
          isUpload: false
        }
      };
    }
    case UIActionTypes.OPEN_FOLLOW_UP_DOCUMENT_MODAL: {
      const {
        sponsorType,
        sponsorKey,
        docType,
        docKey,
        modalTitle = '',
        fileNames,
        isComplete,
        biometricResidencePermit
      } = action.payload;
      return {
        ...state,
        openModal: {
          ...state.openModal,
          biometricResidencePermit,
          fileNames,
          isComplete,
          docType,
          sponsorType,
          sponsorKey,
          docKey,
          modalTitle,
          isInfoOnly: false,
          isFinanceDocumentModal: false,
          isFollowUpDocumentModal: true,
          isUpload: false
        }
      };
    }

    case UIActionTypes.OPEN_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, modalTitle = '', fileNames, metadata } = action.payload;

      const shouldShowRequirements: boolean = metadata?.shouldShowRequirements;

      return {
        ...state,
        openModal: {
          ...state.openModal,
          isFinanceDocumentModal: false,
          isFollowUpModal: false,
          sponsorType,
          sponsorKey,
          docType,
          docKey,
          modalTitle,
          fileNames,
          isUpload: true,
          shouldShowRequirements
        }
      };
    }
    case UIActionTypes.OPEN_FOLLOW_UP_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metadata } = action.payload;

      const shouldShowRequirements: boolean = metadata?.shouldShowRequirements;

      return {
        ...state,
        openModal: {
          ...state.openModal,
          isFinanceDocumentModal: false,
          isFollowUpModal: true,
          sponsorType,
          sponsorKey,
          docType,
          docKey,
          modalTitle,
          fileNames,
          isUpload: true,
          shouldShowRequirements
        }
      };
    }
    case UIActionTypes.OPEN_FINANCE_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metadata } = action.payload;

      const shouldShowRequirements: boolean = metadata?.shouldShowRequirements;

      return {
        ...state,
        openModal: {
          ...state.openModal,
          isFinanceDocumentModal: true,
          sponsorType,
          sponsorKey,
          docType,
          docKey,
          modalTitle,
          fileNames,
          isUpload: true,
          shouldShowRequirements
        }
      };
    }
    case UIActionTypes.CLOSE_MODAL:
      return {
        ...state,
        openModal: null
      };
    case UIActionTypes.UPDATE_SECTIONS: {
      return {
        ...state,
        sections: [...action.payload]
      };
    }
    case UIActionTypes.TOGGLE_TASK_MENU:
      return {
        ...state,
        taskMenuOpen: action.payload.isOpen
      };
    case UIActionTypes.UPDATE_ACTIVE_SECTION_ID: {
      return {
        ...state,
        activeSectionId: action.payload.sectionId
      };
    }
    case StudentActionTypes.SAVE_PERSONAL_INFO:
    case StudentActionTypes.SAVE_HISTORY:
    case StudentActionTypes.SAVE_CONSENTS: {
      const sectionIndex = state.sections.findIndex(section => section.id === SectionId.CHECKLIST);
      const section = state.sections[sectionIndex];
      if (section.status === Status.InProgress) {
        return {
          ...state,
          isLoading: true
        };
      }
      return {
        ...state,
        isLoading: true,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          {
            ...section,
            status: Status.InProgress
          },
          ...state.sections.slice(sectionIndex + 1)
        ]
      };
    }
    case StudentActionTypes.CONFIRM_UPLOAD: {
      const sectionIndex = state.sections.findIndex(section => section.id === SectionId.GENERAL_DOC);
      const section = state.sections[sectionIndex];
      if (section.status === Status.InProgress) {
        return {
          ...state,
          uploadModal: null
        };
      }
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          {
            ...section,
            status: Status.InProgress
          },
          ...state.sections.slice(sectionIndex + 1)
        ],
        uploadModal: null
      };
    }
    case StudentActionTypes.CONFIRM_FINANCE_UPLOAD: {
      const sectionIndex = state.sections.findIndex(section => section.id === SectionId.FINANCE_DOC);
      const section = state.sections[sectionIndex];
      if (section.status === Status.InProgress) {
        return {
          ...state,
          uploadModal: null
        };
      }
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          {
            ...section,
            status: Status.InProgress
          },
          ...state.sections.slice(sectionIndex + 1)
        ],
        uploadModal: null
      };
    }
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW: {
      const sectionIndex = state.sections.findIndex(section => section.id === SectionId.PRE_CAS_INTERVIEW);
      const section = state.sections[sectionIndex];
      if (section.status === Status.InProgress) {
        return {
          ...state
        };
      }
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          {
            ...section,
            status: Status.InProgress
          },
          ...state.sections.slice(sectionIndex + 1)
        ]
      };
    }
    case UIActionTypes.TOGGLE_TAWK_IS_LOADED:
      return {
        ...state,
        isTawkLoaded: action.payload
      };
    case UIActionTypes.UPDATE_INIT_NOTIFICATIONS_SHOWN:
      return {
        ...state,
        initNotificationsShown: action.payload
      };
    case StudentActionTypes.UNSUBSCRIBE:
      return {
        ...state,
        isLoading: true
      };
    case StudentActionTypes.UNSUBSCRIBE_SUCCESS:
      return {
        ...state,
        isLoading: false
      };
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS: {
      const uniConfig: any = deepmerge(defaultConfig, action.payload.config || {});

      if (uniConfig && uniConfig.sections) {
        const {
          CASChecklist,
          GeneralDocument,
          FinanceDocuments,
          PreCASInterview,
          ReceiveCAS,
          VisaApplication,
          ScheduledInterviews
        } = uniConfig.sections;

        const sections = [...state.sections];

        const newSections = sections.map(section => {
          switch (section.id) {
            case SectionId.CHECKLIST: {
              return {
                ...section,
                ...CASChecklist
              };
            }
            case SectionId.GENERAL_DOC: {
              return {
                ...section,
                ...GeneralDocument
              };
            }
            case SectionId.FINANCE_DOC: {
              return {
                ...section,
                ...FinanceDocuments
              };
            }
            case SectionId.PRE_CAS_INTERVIEW: {
              return {
                ...section,
                ...PreCASInterview
              };
            }
            case SectionId.VISA_PREP: {
              return {
                ...section,
                ...VisaApplication
              };
            }
            case SectionId.CAS_DOC: {
              return {
                ...section,
                ...ReceiveCAS
              };
            }
            case SectionId.SCHEDULED_INTERVIEWS: {
              return {
                ...section,
                ...ScheduledInterviews
              };
            }
            default: {
              return {
                ...section
              };
            }
          }
        });

        return {
          ...state,
          sections: newSections,
          isLoading: false
        };
      }
      return state;
    }
    case DataActionTypes.GET_UPLOAD_FILE_URL: {
      const fileName: string = action.payload.fileName;
      const openModal = state.openModal ? { ...state.openModal } : {};
      return {
        ...state,
        openModal: {
          ...openModal,
          fileNames: state.openModal?.fileNames ? [...state.openModal.fileNames, fileName] : [fileName]
        }
      };
    }
    case DataActionTypes.GET_UPLOAD_FILE_URL_FAILURE:
    case DataActionTypes.UPLOAD_FILE_FAILURE: {
      // prepend the latest fileName with "upload--error--" to strip out on document upload
      toast.error(`There was an error updating Student ${action.payload.error || ''}`);
      const openModal = state.openModal ? { ...state.openModal } : {};
      const fileName = (openModal?.fileNames ?? []).length ? openModal.fileNames!.pop() : '';
      return {
        ...state,
        openModal: {
          ...openModal,
          fileNames: state.openModal?.fileNames ? [...state.openModal.fileNames, `upload--error--${fileName}`] : []
        }
      };
    }

    default:
      return state;
  }
};

export { reducer as uiReducer };
